import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { externalCalendarEventsService } from '../api/external-calendar-events.service';
import { externalCalendarEventToCalendarEvent } from '../adapters';
import { EXTERNAL_CALENDAR_EVENTS_KEYS } from './queryKeys';
import type { CalendarEvent, DateRange } from '../types';

/**
 * Unified calendar feed visible in the active tenant — external-provider
 * events (Google/Microsoft), native TimeOff absences (`khronox`), and the
 * caller's own internal events (`internal`). Kept under its original
 * "external" name for historical reasons; see `internal-calendar-events.service.ts`
 * for the internal-events CRUD that mutates this same feed.
 *
 * Tenant-scoped query: X-Tenant-Id determines which Tenant-scoped calendars
 * contribute events (Global-scoped calendars always do), so orgId is part of
 * the query key — unlike useCalendarConnections, which is user-scoped.
 *
 * Cancelled events are filtered out in `select` (product decision: omit, not
 * strikethrough) so the adapter stays a pure 1:1 mapping.
 */
export function useExternalCalendarEvents(
  range: DateRange,
  enabled = true,
): {
  data: CalendarEvent[];
  isLoading: boolean;
  isError: boolean;
} {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const { data, isLoading, isError } = useQuery({
    queryKey: EXTERNAL_CALENDAR_EVENTS_KEYS.range(orgId ?? '', range),
    queryFn: ({ signal }) => externalCalendarEventsService.getExternalEvents(range, signal),
    staleTime: 60_000,
    enabled: !!orgId && enabled,
    select: (raw) =>
      raw
        .filter((e) => e.type !== 'external' || e.status !== 'cancelled')
        .map(externalCalendarEventToCalendarEvent),
  });

  return {
    data: data ?? [],
    isLoading,
    isError,
  };
}
